/**
 * Patrol routes (03 §5.1 step 6): loops that pass the crossings of a linear obstacle and at
 * least one point beside a depression, so that the timing of a crawl up the ravine matters;
 * road patrols between crossings; the belt's wire walk; the compound's perimeter round.
 *
 * Every node is resolved to a free cell and long legs are broken up, because the sim's
 * soldiers path from node to node with a bounded A*.
 */

import { atan2 } from '../sim/math';
import type { Pcg32 } from '../sim/rng';
import type { EnemyKind } from '../sim/types';
import { NO_STAND } from './builder';
import { FLAG } from './grid';
import type { GenCtx, Point } from './types';

const MAX_LEG = 26;

/** Route for a band, as rough points; null when the band offers none. */
function roughRoute(ctx: GenCtx, rng: Pcg32, bandIndex: number): Point[] | null {
  const band = ctx.bands[bandIndex];
  if (!band) return null;
  const { lines, compound: c, player } = ctx.plan;
  const inward = (x: number): number => (x < ctx.sizeX / 2 ? 1 : -1);

  if (band.kind === 'drop') {
    for (let tries = 0; tries < 12; tries++) {
      const x = rng.range(16, ctx.sizeX - 16);
      const z = band.z0 + rng.range(7, 18);
      if ((x - player.x) * (x - player.x) + (z - player.z) * (z - player.z) < 34 * 34) continue;
      return [{ x: x - 9, z }, { x: x + 9, z: z + rng.range(-2, 2) }];
    }
    return null;
  }
  if (band.kind === 'approach') {
    const south = lines[bandIndex - 1];
    const north = lines[bandIndex];
    if (!south || !north) return null;
    const roll = rng.float();
    if (roll < 0.5) {
      // Round both crossings of the line to the north: out through the road gap, back by the ravine.
      const qx = north.quietX + 5 * inward(north.quietX);
      const rx = north.roadX + 1.5 * inward(north.roadX);
      const [near, far] = [north.z + 7, north.z - 7];
      return [{ x: rx, z: near }, { x: rx, z: far }, { x: qx, z: far }, { x: qx, z: near }];
    }
    if (roll < 0.75) {
      return [
        { x: south.roadX + 1.5 * inward(south.roadX), z: south.z - 7 },
        { x: north.roadX + 1.5 * inward(north.roadX), z: north.z + 7 },
      ];
    }
    // Along the ravine, a few metres off its lip.
    return [
      { x: south.quietX + 6 * inward(south.quietX), z: south.z - 7 },
      { x: north.quietX + 6 * inward(north.quietX), z: north.z + 7 },
    ];
  }
  if (band.kind === 'belt') {
    const wire = lines[lines.length - 1];
    if (!wire) return null;
    const z = wire.z - 5.5;
    const quiet = wire.quietX + 6 * inward(wire.quietX);
    return [{ x: quiet, z }, { x: wire.midX, z: z - 2 }, { x: wire.roadX + 5 * inward(wire.roadX), z }];
  }
  if (ctx.spec.layout === 'border') {
    const z = (c.z0 + c.z1) / 2 + rng.range(-3, 3);
    return [{ x: c.x0 + 10, z }, { x: ctx.sizeX / 2, z: z + 2 }, { x: c.x1 - 10, z }];
  }
  const d = ctx.spec.patrolLoop === 'inside' ? -3.5 : 6;
  const x0 = Math.max(3.5, c.x0 - d);
  const x1 = Math.min(ctx.sizeX - 3.5, c.x1 + d);
  const z0 = Math.max(3.5, c.z0 - d);
  const corners: Point[] = [{ x: x0, z: c.z1 + d }, { x: x1, z: c.z1 + d }, { x: x1, z: z0 }, { x: x0, z: z0 }];
  const start = rng.int(4);
  return corners.map((_, i) => corners[(i + start) % 4] ?? { x: x0, z: z0 });
}

/**
 * Break long legs so each is within reach of the soldiers' bounded path search. More than two points
 * make a closed loop; two or fewer an open beat, walked there and back.
 */
export function subdivide(route: Point[]): Point[] {
  const out: Point[] = [];
  const closed = route.length > 2;
  const legs = closed ? route.length : route.length - 1;
  for (let i = 0; i < legs; i++) {
    const a = route[i];
    const b = route[(i + 1) % route.length];
    if (!a || !b) continue;
    out.push(a);
    const length = Math.sqrt((b.x - a.x) * (b.x - a.x) + (b.z - a.z) * (b.z - a.z));
    const pieces = Math.ceil(length / MAX_LEG);
    for (let p = 1; p < pieces; p++) {
      out.push({ x: a.x + ((b.x - a.x) * p) / pieces, z: a.z + ((b.z - a.z) * p) / pieces });
    }
  }
  if (!closed) {
    const last = route[route.length - 1];
    if (last) out.push(last);
    // A there-and-back beat: walk the same nodes home again.
    for (let i = out.length - 2; i >= 1; i--) {
      const p = out[i];
      if (p) out.push(p);
    }
  }
  return out;
}

/**
 * Put a patrol of `kinds.length` soldiers on a route in the band. `startAt` rotates the loop
 * so a second group on the same kind of route sets off from somewhere else.
 */
export function addPatrol(
  ctx: GenCtx, rng: Pcg32, bandIndex: number, kinds: readonly EnemyKind[], startAt = 0,
): boolean {
  const rough = roughRoute(ctx, rng, bandIndex);
  if (!rough) return false;
  const route = subdivide(rough);
  const shift = startAt % route.length;
  const turned = route.map((_, i) => route[(i + shift) % route.length] ?? { x: 0, z: 0 });

  const members: Array<Array<[number, number]>> = [];
  for (let m = 0; m < kinds.length; m++) {
    const nodes: Array<[number, number]> = [];
    for (const p of turned) {
      const spot = ctx.map.nearestOpen(p.x + m * 1.4, p.z + m * 1.1, 4, NO_STAND);
      if (!spot) return false;
      nodes.push(spot);
    }
    members.push(nodes);
  }
  members.forEach((nodes, m) => {
    const first = nodes[0];
    const second = nodes[1] ?? first;
    const kind = kinds[m];
    if (!first || !second || !kind) return;
    const facing = atan2(second[0] - first[0], second[1] - first[1]);
    ctx.map.spawn({ kind, x: first[0], z: first[1], facing, patrol: nodes });
    for (const [x, z] of nodes) ctx.map.mask.circle(x, z, 1.5, FLAG.keep);
  });
  ctx.patrolGroups.push({ band: bandIndex, kinds: [...kinds] });
  const lead = members[0];
  if (lead) {
    const loop = [...lead];
    if (rough.length > 2 && lead[0]) loop.push(lead[0]);
    ctx.map.mask.line(loop, 1.5, FLAG.path);
  }
  return true;
}
